import React, { useContext } from "react";
import MonetizationOnOutlinedIcon from "@mui/icons-material/MonetizationOnOutlined";
import { Box, Typography, useMediaQuery, useTheme } from "@mui/material";
import {
  CustomDivider,
  FlexContainer,
} from "../../../../shared/components/styled/Container.styled";
import { AuthContext } from "../../../../contexts/AuthContext";
import { formatCurrency } from "../../../../shared/utils/currency";
import { NOT_APPLICABLE } from "../../../../shared/constants/Defaults.constants";
import {
  ESCROW_SHORTAGE,
  ONE_TIME_PAYMENT,
} from "../../../../shared/constants/Payments.constants";

// Helper function to safely convert values to numbers
const safeToNumber = (value) => {
  if (value === null || value === undefined || value === '') {
    return 0;
  }
  const num = Number(value);
  return isNaN(num) ? 0 : num;
};

export default function PaymentTotal({
  amountTotal,
  amount,
  additionalPrincipal,
  transactionFee,
  maxTransaction = null,
  type = ONE_TIME_PAYMENT,
}) {
  const { activeLoanDetails } = useContext(AuthContext);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("lg"));

  return (
    <FlexContainer
      padding={isMobile ? "0" : "25px 75px"}
      background={isMobile ? "transparent" : "#d8d8d8"}
      margin={isMobile ? "0" : "0 0 20px 0"}
      gap={isMobile ? "0" : "100px"}
      width={isMobile ? "100%" : "unset"}
      justifycontent={isMobile ? "space-between" : "unset"}
    >
      <FlexContainer
        flexdirection="column"
        height="100%"
        gap={isMobile ? "10px" : "10px"}
        width={isMobile ? "100%" : "unset"}
      >
        <FlexContainer
          width="100%"
          sx={{
            justifyContent: {
              xs: "flex-start",
              md: "space-between",
            },
            flexDirection: {
              xs: "column",
              md: "row",
            },
            alignItems: {
              xs: "flex-start",
              md: "center",
            },
            gap: {
              xs: "4px",
              md: "25px",
            },
          }}
        >
          <Typography
            fontWeight="bold"
            minWidth={isMobile ? "unset" : "200px"}
            textAlign={isMobile ? "start" : "end"}
            color={isMobile ? `${theme.palette.text.secondary}` : "unset"}
            fontSize={isMobile ? "16px" : "unset"}
          >
            Transaction Fee
          </Typography>
          <Typography
            fontWeight="bold"
            minWidth={isMobile ? "unset" : "200px"}
            textAlign="end"
            fontSize={isMobile ? "20px" : "unset"}
            color={isMobile ? `${theme.palette.text.secondary}` : "unset"}
          >
            {formatCurrency(transactionFee)}
          </Typography>
        </FlexContainer>
        <CustomDivider flexItem width="unset" sx={{display: {xs: 'none', md: 'none'}}}/>
        <FlexContainer
          width="100%"
          sx={{
            justifyContent: {
              xs: "flex-start",
              md: "space-between",
            },
            flexDirection: {
              xs: "column",
              md: "row",
            },
            alignItems: {
              xs: "flex-start",
              md: "center",
            },
            gap: {
              xs: "4px",
              md: "25px",
            },
          }}
        >
          <Typography
            fontWeight="bold"
            color="primary"
            minWidth={isMobile ? "unset" : "200px"}
            textAlign={isMobile ? "start" : "end"}
            fontSize={isMobile ? "16px" : "unset"}
          >
            Total Payment Amount
          </Typography>
          <Typography
            fontWeight="bold"
            color="primary"
            minWidth={isMobile ? "unset" : "200px"}
            textAlign="end"
            fontSize={isMobile ? "24px" : "unset"}
          >
            {formatCurrency(amountTotal)}
          </Typography>
        </FlexContainer>
        {type === ONE_TIME_PAYMENT &&
        maxTransaction &&
        safeToNumber(maxTransaction) < safeToNumber(amountTotal) ? (
          <FlexContainer justifycontent="space-between" width="100%" gap="25px">
            <Typography
              fontWeight="bold"
              color="error"
              minWidth={isMobile ? "unset" : "200px"}
              textAlign="end"
            >
              Maximum Permitted Amount
            </Typography>
            <Typography
              fontWeight="bold"
              color="error"
              minWidth={isMobile ? "unset" : "200px"}
              textAlign="end"
            >
              {formatCurrency(maxTransaction)}
            </Typography>
          </FlexContainer>
        ) : (
          ""
        )}
        {safeToNumber(amount) + safeToNumber(additionalPrincipal) >
        activeLoanDetails?.loanDetails?.mortgageBalance ? (
          <FlexContainer justifycontent="space-between" width="100%" gap="25px">
            <Typography
              fontWeight="bold"
              color="error"
              minWidth={isMobile ? "unset" : "200px"}
              maxWidth={isMobile ? "unset" : "200px"}
              textAlign="end"
            >
              Amount + Additional Principal must not exceed
            </Typography>
            <Typography
              fontWeight="bold"
              color="error"
              minWidth={isMobile ? "unset" : "200px"}
              textAlign="end"
            >
              {formatCurrency(activeLoanDetails?.loanDetails?.mortgageBalance)}
            </Typography>
          </FlexContainer>
        ) : (
          ""
        )}
      </FlexContainer>
      {!isMobile && (
        <FlexContainer height="100%" gap="15px">
          {type !== ESCROW_SHORTAGE ? (
            <>
              <MonetizationOnOutlinedIcon fontSize="large" />
              <FlexContainer
                width="100%"
                height="100%"
                flexdirection="column"
                alignitems="flex-start"
              >
                <Typography
                  color="text.hint"
                  fontSize="small"
                  fontWeight="bold"
                >
                  Interest Rate
                </Typography>
                <Typography fontWeight="bold" fontSize={24}>
                  {activeLoanDetails?.loanDetails?.interestRate
                    ? `${activeLoanDetails.loanDetails.interestRate}%`
                    : NOT_APPLICABLE}
                </Typography>
              </FlexContainer>
            </>
          ) : (
            <Box width="120px" />
          )}
        </FlexContainer>
      )}
    </FlexContainer>
  );
}